// frontend/pages/SMSComposer.jsx
import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  EnvelopeIcon, 
  UsersIcon, 
  CurrencyDollarIcon,
  PaperAirplaneIcon,
  PlusIcon,
  TrashIcon,
  DocumentDuplicateIcon,
  ChartBarIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  PencilIcon
} from '@heroicons/react/24/outline';
import { smsService, customerService, mpesaTopupService } from '../services/api';
import { toast } from 'react-hot-toast';

export default function SMSComposer() {
  const [recipients, setRecipients] = useState([]);
  const [message, setMessage] = useState('');
  const [selectedType, setSelectedType] = useState('promotional');
  const [sending, setSending] = useState(false);
  const [selectedTab, setSelectedTab] = useState('compose');
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [topUpAmount, setTopUpAmount] = useState('');
  const [showTopUpModal, setShowTopUpModal] = useState(false);
  const [topUpLoading, setTopUpLoading] = useState(false);
  const [showAddTemplateModal, setShowAddTemplateModal] = useState(false);
  const [showEditTemplateModal, setShowEditTemplateModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [newTemplate, setNewTemplate] = useState({ name: '', message: '', description: '' });
  
  const queryClient = useQueryClient();

  // Fetch customers for recipient selection
  const { data: customers } = useQuery({
    queryKey: ['customers', 'all'],
    queryFn: () => customerService.getCustomers({ limit: 1000 })
  });

  // Fetch SMS history
  const { data: smsHistory, isLoading: historyLoading } = useQuery({
    queryKey: ['sms', 'history'],
    queryFn: () => smsService.getHistory({ limit: 100 }),
    enabled: selectedTab === 'history'
  });

  // Fetch SMS balance
  const { data: balance, refetch: refetchBalance } = useQuery({
    queryKey: ['sms', 'balance'],
    queryFn: () => smsService.getBalance(),
    refetchInterval: 30000
  });

  // Fetch templates from backend (includes both default and custom)
  const { data: templatesData, refetch: refetchTemplates } = useQuery({
    queryKey: ['sms', 'templates'],
    queryFn: () => smsService.getTemplates(),
    enabled: selectedTab === 'templates'
  });

  // Search customers
  useEffect(() => {
    if (searchTerm.length > 1) {
      const filtered = customers?.data?.filter(c => 
        c.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
        c.phone.includes(searchTerm)
      ) || [];
      setSearchResults(filtered);
      setShowSearchResults(true);
    } else {
      setSearchResults([]);
      setShowSearchResults(false);
    }
  }, [searchTerm, customers]);

  // Send SMS mutation
  const sendSMSMutation = useMutation({
    mutationFn: (data) => smsService.sendBulkSMS(data),
    onSuccess: (response) => {
      toast.success(`SMS sent successfully to ${response.summary.successful} recipients`);
      setMessage('');
      setRecipients([]);
      refetchBalance();
      queryClient.invalidateQueries(['sms', 'history']);
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to send SMS');
    }
  });

  // Create custom template mutation
  const createTemplateMutation = useMutation({
    mutationFn: (data) => smsService.createCustomTemplate(data),
    onSuccess: () => {
      toast.success('Template created successfully');
      setShowAddTemplateModal(false);
      setNewTemplate({ name: '', message: '', description: '' });
      refetchTemplates();
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to create template');
    }
  });

  // Update custom template mutation
  const updateTemplateMutation = useMutation({
    mutationFn: ({ id, data }) => smsService.updateCustomTemplate(id, data),
    onSuccess: () => {
      toast.success('Template updated successfully');
      setShowEditTemplateModal(false);
      setEditingTemplate(null);
      refetchTemplates();
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to update template');
    }
  });

  // Delete custom template mutation
  const deleteTemplateMutation = useMutation({
    mutationFn: (id) => smsService.deleteCustomTemplate(id),
    onSuccess: () => {
      toast.success('Template deleted successfully');
      refetchTemplates();
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to delete template');
    }
  });

// Simplified topUpMutation - No polling, rely on webhook
const topUpMutation = useMutation({
    mutationFn: (amount) => mpesaTopupService.initiateTopUp({
        phoneNumber: prompt('Enter M-Pesa phone number for payment:', '0712345678'),
        amount: amount
    }),
    onSuccess: (response) => {
        if (response.success) {
            toast.success('STK Push sent! Check your phone to complete payment.');
            toast('Balance will update automatically once payment is confirmed.', { duration: 8000, icon: '⏳' });
            
            // Close modal and let webhook handle the rest
            setShowTopUpModal(false);
            setTopUpAmount('');
            
            // Refresh balance after a delay (webhook will update it)
            setTimeout(() => {
                refetchBalance();
                toast.success('If payment was successful, your balance has been updated.', { duration: 5000 });
            }, 15000);
        } else {
            toast.error(response.message || 'Failed to initiate payment');
        }
        setTopUpLoading(false);
    },
    onError: (error) => {
        toast.error(error.response?.data?.error || 'Failed to process top up');
        setTopUpLoading(false);
    }
});

  const templates = templatesData?.data || [];

  const addRecipients = (type, ids = null) => {
    if (type === 'all_customers') {
      const allCustomers = customers?.data || [];
      const newRecipients = allCustomers.map(c => ({
        id: c.id,
        name: c.name,
        phone: c.phone,
        balance: c.current_balance
      }));
      setRecipients(newRecipients);
      toast.success(`Added ${newRecipients.length} customers`);
    } else if (type === 'overdue') {
      const overdueCustomers = customers?.data?.filter(c => c.current_balance > 0) || [];
      setRecipients(overdueCustomers);
      toast.success(`Added ${overdueCustomers.length} overdue customers`);
    }
  };

  const addSelectedCustomer = (customer) => {
    if (!recipients.find(r => r.id === customer.id)) {
      setRecipients([...recipients, {
        id: customer.id,
        name: customer.name,
        phone: customer.phone,
        balance: customer.current_balance
      }]);
      toast.success(`Added ${customer.name}`);
    } else {
      toast.error(`${customer.name} already in list`);
    }
    setSearchTerm('');
    setShowSearchResults(false);
  };

  const removeRecipient = (index) => {
    setRecipients(prev => prev.filter((_, i) => i !== index));
  };

  const clearRecipients = () => {
    setRecipients([]);
  };

  const applyTemplate = (template) => {
    setMessage(template.message);
  };

  const handleEditTemplate = (template) => {
    setEditingTemplate(template);
    setShowEditTemplateModal(true);
  };

  const handleDeleteTemplate = (template) => {
    if (window.confirm(`Are you sure you want to delete template "${template.name}"?`)) {
      deleteTemplateMutation.mutate(template.id);
    }
  };

  const handleUpdateTemplate = () => {
    if (!editingTemplate.name || !editingTemplate.message) {
      toast.error('Template name and message are required');
      return;
    }
    updateTemplateMutation.mutate({
      id: editingTemplate.id,
      data: {
        name: editingTemplate.name,
        message: editingTemplate.message,
        description: editingTemplate.description
      }
    });
  };

  const handleSend = async () => {
    if (recipients.length === 0) {
      toast.error('Please select at least one recipient');
      return;
    }

    if (!message.trim()) {
      toast.error('Please enter a message');
      return;
    }

    const recipientsWithData = recipients.map(r => ({
      id: r.id,
      name: r.name,
      phone: r.phone,
      balance: r.balance,
      invoice_number: r.invoice_number,
      due_date: r.due_date
    }));

    const messageParts = Math.ceil(message.length / 160);
    const estimatedCost = messageParts * 1.5 * recipients.length;
    
    if (balance?.balance < estimatedCost) {
      toast.error(`Insufficient SMS balance. Need KES ${estimatedCost.toFixed(2)}`);
      return;
    }

    setSending(true);
    try {
      await sendSMSMutation.mutateAsync({
        recipients: recipientsWithData,
        message,
        type: selectedType
      });
    } finally {
      setSending(false);
    }
  };

  const handleTopUp = () => {
    const amount = parseFloat(topUpAmount);
    if (isNaN(amount) || amount < 10) {
      toast.error('Please enter a valid amount (minimum KES 10)');
      return;
    }
    setTopUpLoading(true);
    topUpMutation.mutate(amount);
  };

  const handleCreateTemplate = () => {
    if (!newTemplate.name || !newTemplate.message) {
      toast.error('Template name and message are required');
      return;
    }
    createTemplateMutation.mutate(newTemplate);
  };

  const getCharacterCount = () => {
    const length = message.length;
    const parts = Math.ceil(length / 160);
    const remaining = 160 - (length % 160);
    return { length, parts, remaining: remaining === 160 ? 0 : remaining };
  };

  const chars = getCharacterCount();
  const costPerSms = 1.5;

  // Separate templates into default and custom
  const defaultTemplates = templates.filter(t => t.type === 'default' || (typeof t.id === 'number' && t.id <= 10));
  const customTemplates = templates.filter(t => t.type === 'custom' || (typeof t.id === 'string' && t.id.length === 36));

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">SMS Composer</h1>
        <p className="text-gray-600">Send bulk SMS to customers and manage communications</p>
      </div>

      {/* SMS Balance Card */}
      <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg shadow-lg p-6 mb-8 text-white">
        <div className="flex justify-between items-center">
          <div>
            <p className="text-blue-100">Available SMS Balance</p>
            <p className="text-3xl font-bold">KES {balance?.balance?.toLocaleString() || 0}</p>
            <p className="text-sm text-blue-100 mt-1">
              ~ {Math.floor((balance?.balance || 0) / costPerSms)} SMS messages (KES {costPerSms}/SMS)
            </p>
          </div>
          <button
            onClick={() => setShowTopUpModal(true)}
            className="px-4 py-2 bg-white text-blue-600 rounded-lg hover:bg-blue-50 transition-colors"
          >
            Top Up
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="flex space-x-8">
          <button
            onClick={() => setSelectedTab('compose')}
            className={`pb-4 px-1 text-sm font-medium border-b-2 transition-colors ${
              selectedTab === 'compose'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <PaperAirplaneIcon className="h-5 w-5 inline mr-2" />
            Compose SMS
          </button>
          <button
            onClick={() => setSelectedTab('history')}
            className={`pb-4 px-1 text-sm font-medium border-b-2 transition-colors ${
              selectedTab === 'history'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <ChartBarIcon className="h-5 w-5 inline mr-2" />
            SMS History
          </button>
          <button
            onClick={() => setSelectedTab('templates')}
            className={`pb-4 px-1 text-sm font-medium border-b-2 transition-colors ${
              selectedTab === 'templates'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <DocumentDuplicateIcon className="h-5 w-5 inline mr-2" />
            Templates
          </button>
        </nav>
      </div>

      {/* Compose Tab */}
      {selectedTab === 'compose' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Recipients */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">Recipients</h3>
                <span className="text-sm text-gray-500">{recipients.length} selected</span>
              </div>

              <div className="space-y-2 mb-4">
                <button
                  onClick={() => addRecipients('all_customers')}
                  className="w-full flex items-center justify-between px-3 py-2 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <span className="flex items-center">
                    <UsersIcon className="h-5 w-5 mr-2 text-blue-500" />
                    All Customers
                  </span>
                  <span className="text-sm text-gray-500">{customers?.total || 0}</span>
                </button>
                <button
                  onClick={() => addRecipients('overdue')}
                  className="w-full flex items-center justify-between px-3 py-2 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <span className="flex items-center">
                    <CurrencyDollarIcon className="h-5 w-5 mr-2 text-red-500" />
                    Overdue Accounts
                  </span>
                  <span className="text-sm text-gray-500">
                    {customers?.data?.filter(c => c.current_balance > 0).length || 0}
                  </span>
                </button>
              </div>

              {/* Search Customers */}
              <div className="mb-4 relative">
                <div className="relative">
                  <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search customers by name or phone..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-9 pr-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                
                {showSearchResults && searchResults.length > 0 && (
                  <div className="absolute z-10 mt-1 w-full bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {searchResults.map(customer => (
                      <button
                        key={customer.id}
                        onClick={() => addSelectedCustomer(customer)}
                        className="w-full text-left px-3 py-2 hover:bg-gray-50 border-b last:border-b-0 transition-colors"
                      >
                        <div className="font-medium text-sm">{customer.name}</div>
                        <div className="text-xs text-gray-500">{customer.phone}</div>
                        {customer.current_balance > 0 && (
                          <div className="text-xs text-red-500">Balance: KES {customer.current_balance.toLocaleString()}</div>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Recipients List */}
              <div className="max-h-96 overflow-y-auto space-y-2">
                {recipients.length === 0 ? (
                  <p className="text-center text-gray-500 py-8">No recipients selected</p>
                ) : (
                  recipients.map((recipient, index) => (
                    <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                      <div className="flex-1">
                        <p className="text-sm font-medium">{recipient.name}</p>
                        <p className="text-xs text-gray-500">{recipient.phone}</p>
                        {recipient.balance > 0 && (
                          <p className="text-xs text-red-600">Balance: KES {recipient.balance.toLocaleString()}</p>
                        )}
                      </div>
                      <button
                        onClick={() => removeRecipient(index)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  ))
                )}
              </div>

              {recipients.length > 0 && (
                <button
                  onClick={clearRecipients}
                  className="mt-4 w-full text-sm text-red-600 hover:text-red-700"
                >
                  Clear All
                </button>
              )}
            </div>
          </div>

          {/* Right Column - Message Composer */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow p-6">
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Message Type
                </label>
                <select
                  value={selectedType}
                  onChange={(e) => setSelectedType(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="promotional">Promotional</option>
                  <option value="debt_reminder">Debt Reminder</option>
                  <option value="payment_alert">Payment Alert</option>
                  <option value="otp">OTP Verification</option>
                </select>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Message
                </label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={6}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                  placeholder="Type your message here... Use {customer_name}, {business_name}, etc. for personalization"
                />
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>{chars.length} characters</span>
                  <span>{chars.parts} message part(s)</span>
                  {chars.remaining > 0 && <span>{chars.remaining} characters remaining in this part</span>}
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Available Variables
                </label>
                <div className="flex flex-wrap gap-2">
                  {['{customer_name}', '{business_name}', '{invoice_number}', '{amount}', '{due_date}'].map(varName => (
                    <button
                      key={varName}
                      onClick={() => setMessage(prev => prev + ' ' + varName)}
                      className="px-2 py-1 bg-gray-100 text-gray-700 text-sm rounded hover:bg-gray-200"
                    >
                      {varName}
                    </button>
                  ))}
                </div>
              </div>

              {message && recipients.length > 0 && (
                <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm font-medium text-gray-700 mb-2">Preview (first recipient)</p>
                  <div className="bg-white p-3 rounded border">
                    <p className="text-sm">
                      {message.replace(/{customer_name}/g, recipients[0]?.name || 'Customer')}
                    </p>
                  </div>
                </div>
              )}

              <div className="flex space-x-3">
                <button
                  onClick={handleSend}
                  disabled={sending || recipients.length === 0 || !message.trim()}
                  className="flex-1 flex justify-center items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {sending ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Sending...
                    </>
                  ) : (
                    <>
                      <PaperAirplaneIcon className="h-5 w-5 mr-2" />
                      Send SMS
                    </>
                  )}
                </button>
              </div>

              {recipients.length > 0 && message && (
                <div className="mt-4 p-3 bg-yellow-50 rounded-lg">
                  <p className="text-sm text-yellow-800">
                    <strong>Estimated Cost:</strong> KES {(chars.parts * costPerSms * recipients.length).toFixed(2)}
                    <br />
                    <span className="text-xs">Based on {chars.parts} message part(s) × KES {costPerSms} × {recipients.length} recipient(s)</span>
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* History Tab */}
      {selectedTab === 'history' && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="p-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold">SMS History</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Recipient</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Message</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cost</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {historyLoading ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-4 text-center text-gray-500">
                      Loading...
                    </td>
                  </tr>
                ) : smsHistory?.data?.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-4 text-center text-gray-500">
                      No SMS history found
                    </td>
                  </tr>
                ) : (
                  smsHistory?.data?.map((sms) => {
                    const costValue = typeof sms.cost === 'number' ? sms.cost : parseFloat(sms.cost) || 0;
                    return (
                      <tr key={sms.id}>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          {new Date(sms.created_at).toLocaleString()}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">{sms.recipient}</td>
                        <td className="px-6 py-4 text-sm text-gray-600 max-w-md truncate" title={sms.message}>
                          {sms.message.length > 100 ? sms.message.substring(0, 100) + '...' : sms.message}
                        </td>
                        <td className="px-6 py-4">
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            sms.type === 'debt_reminder' ? 'bg-red-100 text-red-800' :
                            sms.type === 'payment_alert' ? 'bg-green-100 text-green-800' :
                            sms.type === 'otp' ? 'bg-purple-100 text-purple-800' :
                            'bg-blue-100 text-blue-800'
                          }`}>
                            {sms.type?.replace(/_/g, ' ') || 'promotional'}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            sms.status === 'sent' ? 'bg-green-100 text-green-800' :
                            sms.status === 'delivered' ? 'bg-blue-100 text-blue-800' :
                            sms.status === 'failed' ? 'bg-red-100 text-red-800' :
                            'bg-yellow-100 text-yellow-800'
                          }`}>
                            {sms.status || 'pending'}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          KES {costValue.toFixed(2)}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Templates Tab */}
      {selectedTab === 'templates' && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">SMS Templates</h3>
            <button
              onClick={() => setShowAddTemplateModal(true)}
              className="flex items-center px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700"
            >
              <PlusIcon className="h-4 w-4 mr-1" />
              Add Template
            </button>
          </div>

          {/* Default Templates Section */}
          {defaultTemplates.length > 0 && (
            <div className="mb-8">
              <h4 className="text-md font-medium text-gray-700 mb-3">Default Templates</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {defaultTemplates.map((template) => (
                  <div key={template.id} className="bg-white rounded-lg shadow p-4 hover:shadow-md transition-shadow">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <h4 className="font-semibold text-gray-900">{template.name}</h4>
                        {template.description && (
                          <p className="text-xs text-gray-500 mt-0.5">{template.description}</p>
                        )}
                      </div>
                      <button
                        onClick={() => applyTemplate(template)}
                        className="text-blue-600 hover:text-blue-700 text-sm"
                      >
                        Use Template
                      </button>
                    </div>
                    <p className="text-sm text-gray-600 line-clamp-2">{template.message}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Custom Templates Section */}
          {customTemplates.length > 0 && (
            <div>
              <h4 className="text-md font-medium text-gray-700 mb-3">Custom Templates</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {customTemplates.map((template) => (
                  <div key={template.id} className="bg-white rounded-lg shadow p-4 hover:shadow-md transition-shadow border border-gray-200">
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex-1">
                        <h4 className="font-semibold text-gray-900">{template.name}</h4>
                        {template.description && (
                          <p className="text-xs text-gray-500 mt-0.5">{template.description}</p>
                        )}
                      </div>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleEditTemplate(template)}
                          className="text-gray-500 hover:text-gray-700"
                          title="Edit"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteTemplate(template)}
                          className="text-red-500 hover:text-red-700"
                          title="Delete"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => applyTemplate(template)}
                          className="text-blue-600 hover:text-blue-700 text-sm"
                        >
                          Use
                        </button>
                      </div>
                    </div>
                    <p className="text-sm text-gray-600 line-clamp-2">{template.message}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {templates.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              <DocumentDuplicateIcon className="h-12 w-12 mx-auto mb-3 text-gray-300" />
              <p>No templates available</p>
              <button
                onClick={() => setShowAddTemplateModal(true)}
                className="mt-3 text-blue-600 hover:text-blue-700"
              >
                Create your first template
              </button>
            </div>
          )}
        </div>
      )}

      {/* Top Up Modal */}
      {showTopUpModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold mb-4">Top Up SMS Balance</h3>
            <p className="text-sm text-gray-600 mb-4">
              SMS cost: <strong>KES {costPerSms}</strong> per message part (160 characters)
            </p>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Amount (KES)
              </label>
              <input
                type="number"
                value={topUpAmount}
                onChange={(e) => setTopUpAmount(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Minimum KES 10"
                min="10"
                step="10"
              />
              <p className="text-xs text-gray-500 mt-1">
                You will receive approximately {Math.floor(parseFloat(topUpAmount) / costPerSms)} SMS messages
              </p>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={() => setShowTopUpModal(false)}
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={handleTopUp}
                disabled={topUpLoading}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {topUpLoading ? 'Processing...' : 'Pay via M-Pesa'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Template Modal */}
      {showAddTemplateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-lg w-full">
            <h3 className="text-lg font-semibold mb-4">Create Custom Template</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Template Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={newTemplate.name}
                  onChange={(e) => setNewTemplate({ ...newTemplate, name: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., Customer Appreciation"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <input
                  type="text"
                  value={newTemplate.description}
                  onChange={(e) => setNewTemplate({ ...newTemplate, description: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Brief description of when to use this template"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Message <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={newTemplate.message}
                  onChange={(e) => setNewTemplate({ ...newTemplate, message: e.target.value })}
                  rows={4}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Type your message template here... Use {customer_name}, {business_name}, etc."
                />
                <p className="text-xs text-gray-500 mt-1">
                  Available variables: {'{customer_name}'}, {'{business_name}'}, {'{invoice_number}'}, {'{amount}'}, {'{due_date}'}
                </p>
              </div>
            </div>
            <div className="flex space-x-3 mt-6">
              <button
                onClick={() => setShowAddTemplateModal(false)}
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateTemplate}
                disabled={createTemplateMutation.isPending}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {createTemplateMutation.isPending ? 'Creating...' : 'Create Template'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Template Modal */}
      {showEditTemplateModal && editingTemplate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-lg w-full">
            <h3 className="text-lg font-semibold mb-4">Edit Template</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Template Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={editingTemplate.name}
                  onChange={(e) => setEditingTemplate({ ...editingTemplate, name: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <input
                  type="text"
                  value={editingTemplate.description || ''}
                  onChange={(e) => setEditingTemplate({ ...editingTemplate, description: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Message <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={editingTemplate.message}
                  onChange={(e) => setEditingTemplate({ ...editingTemplate, message: e.target.value })}
                  rows={4}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Available variables: {'{customer_name}'}, {'{business_name}'}, {'{invoice_number}'}, {'{amount}'}, {'{due_date}'}
                </p>
              </div>
            </div>
            <div className="flex space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowEditTemplateModal(false);
                  setEditingTemplate(null);
                }}
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateTemplate}
                disabled={updateTemplateMutation.isPending}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {updateTemplateMutation.isPending ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}